import { TUNABLES } from "../config";
import { type Command, playerSeatId, type RunState, type SeatId } from "../core";
import { applyEntry, hashDue } from "./lockstep";
import type {
  RejectReason,
  RoomClient,
  RoomMode,
  SeatedPlayer,
  Snapshot,
  TurnEntry,
} from "./protocol";
import { sha256Hex } from "./sha256";

/**
 * The page's end of a room match (B3.6, D-024, D-026, D-027). Every page runs the core on the
 * room's turns: turn n is taken at tick n, through the door, with the seat the room stamped. A
 * turn that has not come holds the page at its tick, and the turns that came while it waited (or
 * while it loaded the game) are taken as fast as the caller lets them. The page's own commands
 * go to the room, never into its state; they come back stamped in a turn like everyone's. Every
 * 8 ticks it tells the room the hash of its state (K7.1: the SHA-256 of its text). Browser code
 * with the wire and the clock given to it, so the tests drive it by hand. Since B3.7 a turn may
 * carry a change of who holds the seats (a seat left to its script, or taken back), and the
 * page's link to the room may drop
 * and come back (`roomClient.ts`): the match keeps its turns and its place through it. K4: it keeps
 * the text of the states it last hashed and sends the room the one it offers for; told it is out of
 * step, it starts its next follow from the room's snapshot and takes the turns after it.
 */
export interface MatchStats {
  /** The socket's round trips, ping to pong, in ms. */
  readonly roundTripsMs: readonly number[];
  /** From a command sent to the moment its turn was taken on this page, in ms. */
  readonly effectsMs: readonly number[];
  /** The ticks the room flagged: the pages' states differed there (D-027). */
  readonly desyncs: readonly number[];
  /** Turns that came more than `TUNABLES.net.lateTurnMs` after the one before: the page waited. */
  readonly lateTurns: number;
  /** The most turns held beyond the page's tick at once: how far it fell behind. */
  readonly mostBehind: number;
  /** The last hashes this page reported, `[tick, hash]`: two pages compare them (B3.7). */
  readonly hashes: readonly (readonly [number, string])[];
  /** Times the room's socket dropped and came back (B3.7). */
  readonly rejoins: number;
  /**
   * K2: every change of who holds the seats this page took, `[tick, held]` at the tick it took
   * it: the pages of one match compare them to prove a seat changed hands at one tick everywhere.
   */
  readonly heldChanges: readonly (readonly [number, readonly SeatId[]])[];
  /** K4: the snapshots this page sent the room when it asked. */
  readonly uploads: number;
  /** K4: the times the room set this page level again from its snapshot. */
  readonly resyncs: number;
}

/**
 * K4: a snapshot's state, or undefined when its text does not hash to its hash: what came is not
 * what the room's majority voted.
 */
export function stateOfSnapshot(snapshot: Snapshot): RunState | undefined {
  if (sha256Hex(snapshot.body) !== snapshot.hash) {
    return undefined;
  }
  try {
    return JSON.parse(snapshot.body) as RunState;
  } catch {
    return undefined;
  }
}

/** What the room said that the game reacts to. */
export type MatchNews =
  | { readonly kind: "turns" }
  | { readonly kind: "desync"; readonly tick: number }
  /** B3.7: the room's socket dropped; the page tries to come back. */
  | { readonly kind: "dropped" }
  /** B3.7: the page is back at the table; the turns it missed follow. */
  | { readonly kind: "back" }
  /** B3.7: the room could not be reached again: the page plays on alone (the kill switch). */
  | { readonly kind: "lost" }
  /** K4: the page was out of step; its next follow starts from the room's snapshot. */
  | { readonly kind: "resync" }
  /** K4: someone sat down under way: the room's table changed (`RoomMatch.table`). */
  | { readonly kind: "table" };

export interface Match {
  /** The seat this page's human holds (K0: its number at the room's table). */
  readonly seat: SeatId;
  /** Calls `listener` whenever turns come or the room flags a desync. */
  watch(listener: (news: MatchNews) => void): void;
  /** Turns from the room: `turns[i]` is turn `from + i`. */
  hold(from: number, turns: readonly (readonly TurnEntry[])[]): void;
  /** The last turn the page has taken, for a rejoin to start after; undefined before the first. */
  lastTaken(): number | undefined;
  /** The link's news (B3.7): dropped, back at the table, or lost for good; K4: a new table. */
  link(news: "dropped" | "back" | "lost" | "table"): void;
  /** The turns held beyond the state's tick. */
  behind(state: RunState): number;
  /**
   * Takes the held turns the state can follow, at most `limit`, each through the door and then
   * `step` (the core's tick, with whatever the game reads off it). Stops at a turn not yet held.
   */
  follow(state: RunState, limit: number, step: (state: RunState) => RunState): RunState;
  /** A command of this page's seat: to the room, timed until its turn is taken. */
  send(command: Command): void;
  /** The room refused a message; a refused command will not come back. */
  refused(reason: RejectReason): void;
  ping(): void;
  pong(sentAt: number): void;
  desync(tick: number): void;
  /** K4: the room asks for this page's state at `tick`; sent if the page still keeps its text. */
  offer(tick: number): void;
  /**
   * K4: the room says this page is out of step: its next `follow` starts from the snapshot and
   * takes the turns from its tick. False, and nothing changes, when the text does not hash.
   */
  resync(snapshot: Snapshot): boolean;
  stats(): MatchStats;
}

export function createMatch(options: {
  readonly seat: SeatId;
  readonly send: (message: RoomClient) => void;
  /** Milliseconds on the page's clock. */
  readonly now: () => number;
}): Match {
  const { seat, send, now } = options;
  const held = new Map<number, readonly TurnEntry[]>();
  const hashes: (readonly [number, string])[] = [];
  const heldChanges: (readonly [number, readonly SeatId[]])[] = [];
  let rejoins = 0;
  /** K4: the text of each state this page hashed lately, `[tick, text]`, oldest first. */
  const texts: (readonly [number, string])[] = [];
  let uploads = 0;
  let resyncs = 0;
  /** K4: the room's snapshot the next `follow` starts from instead of the state it is given. */
  let restart: RunState | undefined;
  /** When each command of this page still on its way was sent, oldest first. */
  const inFlight: number[] = [];
  const roundTripsMs: number[] = [];
  const effectsMs: number[] = [];
  const desyncs: number[] = [];
  let lastTurnAt: number | undefined;
  let lateTurns = 0;
  let mostBehind = 0;
  /** The first turn the page has not taken: a turn before it is a replay's overlap. */
  let untaken = 0;
  const listeners: ((news: MatchNews) => void)[] = [];
  const tell = (news: MatchNews) => {
    for (const listener of listeners) {
      listener(news);
    }
  };

  return {
    seat,
    watch(listener) {
      listeners.push(listener);
    },
    hold(from, turns) {
      const arrived = now();
      if (lastTurnAt !== undefined && arrived - lastTurnAt > TUNABLES.net.lateTurnMs) {
        lateTurns += 1;
      }
      lastTurnAt = arrived;
      turns.forEach((turn, index) => {
        const at = from + index;
        if (at >= untaken) {
          held.set(at, turn);
        }
      });
      tell({ kind: "turns" });
    },
    behind: (state) => [...held.keys()].filter((at) => at >= state.tick).length,
    lastTaken: () => (untaken > 0 ? untaken - 1 : undefined),
    link(news) {
      if (news === "back") {
        rejoins += 1;
      }
      tell({ kind: news });
    },
    follow(state, limit, step) {
      let next = restart ?? state;
      restart = undefined;
      for (const at of held.keys()) {
        if (at < next.tick) {
          held.delete(at);
        }
      }
      mostBehind = Math.max(mostBehind, held.size);
      for (let taken = 0; taken < limit && next.status === "running"; taken += 1) {
        const turn = held.get(next.tick);
        if (!turn) {
          break;
        }
        held.delete(next.tick);
        for (const entry of turn) {
          if ("seat" in entry && entry.seat === seat) {
            const sentAt = inFlight.shift();
            if (sentAt !== undefined) {
              effectsMs.push(Math.round(now() - sentAt));
            }
          } else if ("held" in entry) {
            heldChanges.push([next.tick, entry.held]);
          }
          next = applyEntry(next, entry);
        }
        next = step(next);
        // A tick long past (a replay after a rejoin) is not reported: the room settled it, and a
        // burst of old hashes would only spend the page's messages.
        if (hashDue(next.tick) && held.size <= TUNABLES.net.hashEveryTicks) {
          // `voteOf`, with its text kept: the room may ask for the state its vote hashed (K4).
          const text = JSON.stringify(next);
          const hash = sha256Hex(text);
          send({ type: "hash", tick: next.tick, hash });
          hashes.push([next.tick, hash]);
          if (hashes.length > TUNABLES.net.hashesKept) {
            hashes.shift();
          }
          texts.push([next.tick, text]);
          if (texts.length > TUNABLES.net.snapshotTextsKept) {
            texts.shift();
          }
        }
      }
      untaken = next.tick;
      return next;
    },
    send(command) {
      inFlight.push(now());
      send({ type: "command", command });
    },
    refused(reason) {
      if (reason === "rate") {
        inFlight.pop();
      }
    },
    ping() {
      send({ type: "ping", sentAt: Math.round(now()) });
    },
    pong(sentAt) {
      roundTripsMs.push(Math.round(now()) - sentAt);
    },
    desync(tick) {
      desyncs.push(tick);
      tell({ kind: "desync", tick });
    },
    offer(tick) {
      const kept = texts.find(([at]) => at === tick);
      if (kept) {
        uploads += 1;
        send({ type: "snapshot", tick, body: kept[1] });
      }
    },
    resync(snapshot) {
      const from = stateOfSnapshot(snapshot);
      if (!from) {
        return false;
      }
      restart = from;
      held.clear();
      untaken = from.tick;
      resyncs += 1;
      // What this page counted from the snapshot's tick on is counted again as it replays.
      const keep = <T extends readonly [number, ...unknown[]]>(list: T[]) =>
        list.splice(0, list.length, ...list.filter(([tick]) => tick < from.tick));
      keep(hashes);
      keep(heldChanges);
      keep(texts);
      tell({ kind: "resync" });
      return true;
    },
    stats: () => ({
      roundTripsMs,
      effectsMs,
      desyncs,
      lateTurns,
      mostBehind,
      hashes,
      rejoins,
      heldChanges,
      uploads,
      resyncs,
    }),
  };
}

/** The page's hold on its room after the start (B3.7): for a hook, a test, or a Leave. */
export interface RoomLink {
  /** K4.1: seated, before the start: asks the room to start now. Before the seat or after the start, nothing. */
  ready(): void;
  /** Gets up: the seat goes to its script at once, and the link stays down until `rejoin`. */
  leave(): void;
  /** Sits down again after a `leave`: the seat is taken back and the missed turns replayed. */
  rejoin(): void;
  /** Closes the socket as a network fault would; the link comes back by itself. */
  drop(): void;
}

/** A room match as the game takes it at the start: the page's end of it, and who holds what. */
export interface RoomMatch {
  readonly match: Match;
  /** K2: the room's mode: the run every page builds from the seed. */
  readonly mode: RoomMode;
  /** The seats humans held at the start: the shared state's held seats. */
  readonly held: readonly SeatId[];
  /**
   * K3: who sat where, the names by seat, as the room last told it (K4: a seat taken under way
   * too, news "table"). The screen's names come from here, never from the core: the run's state
   * holds no name.
   */
  readonly table: readonly SeatedPlayer[];
  readonly link: RoomLink;
  /**
   * K4: the state the shared run starts from when the page came in under way or back from a
   * reload: the room's latest snapshot, checked by its hash. Undefined: the seed's run.
   */
  readonly snapshot: RunState | undefined;
}

/**
 * The shared state as one page's screen reads it (K0): that page's seat named the viewer, so
 * `playerSeat` and `seatFaction` name it. No rule reads the viewer, so every page keeps one state
 * and hashes it as it is; only its screen sees this. The state itself when the page's seat is
 * the one the screen would play anyway.
 */
export function viewFor(state: RunState, seat: SeatId): RunState {
  return playerSeatId(state) === seat ? state : { ...state, viewer: seat };
}
